import { BookmarkPlus } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { SaveSegmentDialog } from "@/components/crew/save-segment-dialog"
import { Button } from "@/components/ui/button"
import type { CrewFilters } from "@/types/crew"

/**
 * OKS-28 — "Save as segment" button (UX Spec §2.1).
 *
 * Visible ONLY when `(filters has any active rule) && !applied_segment`
 * — the "save the current filter set" affordance is meaningless when
 * a segment is already applied (the segment IS the saved filter set)
 * or when no filters are active (nothing to save).
 *
 * Wires a controlled `<SaveSegmentDialog>` — opens on click, closes
 * on submit success (the BE 303 navigates the whole page) or on
 * Cancel / overlay / Esc.
 *
 * **Locked testid:** `crew-list-segment-save` (UX Spec §2.1).
 */
export interface SaveSegmentButtonProps {
  filters: CrewFilters
  appliedSegment: { id: number; name: string } | null
  /**
   * Forwarded to the trigger Button. Used by the mobile Sheet footer
   * to render the button full-width per UX Spec §5 (`<640px | Save
   * action | Inside Sheet footer, full-width button`).
   */
  className?: string
}

function hasAnyFilterActive(filters: CrewFilters): boolean {
  if ((filters.q ?? "").trim() !== "") return true
  if ((filters.location ?? "").trim() !== "") return true
  if (filters.tags.length > 0) return true
  if (typeof filters.followers_min === "number") return true
  if (typeof filters.followers_max === "number") return true
  if ((filters.joined_from ?? "").trim() !== "") return true
  if ((filters.joined_to ?? "").trim() !== "") return true
  if (filters.at_risk) return true
  return false
}

export function SaveSegmentButton({
  filters,
  appliedSegment,
  className,
}: SaveSegmentButtonProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  // UX Spec §2.1 — visible only when ≥1 filter rule is active AND no
  // segment currently applied.
  if (appliedSegment !== null) return null
  if (!hasAnyFilterActive(filters)) return null

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="crew-list-segment-save"
        onClick={() => setOpen(true)}
        className={className}
      >
        <BookmarkPlus className="size-4" aria-hidden="true" />
        {t("crew_segments.save.button")}
      </Button>
      <SaveSegmentDialog open={open} onOpenChange={setOpen} filters={filters} />
    </>
  )
}
